/**
 * Free Gift — Vaillant Parfums
 * -----------------------------------------------------------------------------
 * Automatically adds a 0€ gift line item (Kit Prestige "Offert") to the cart
 * whenever a qualifying product (L'Original 100ML) is present, and removes it
 * when no qualifying product remains. The gift quantity is forced to 1 and the
 * line cannot be modified by the customer (no qty / no remove in the cart UI).
 *
 * All configuration lives in `snippets/free-gift.liquid` as `window.FREE_GIFT`.
 * To disable the campaign: set `enabled: false` there (single source of truth) —
 * the script then stops AND removes any leftover gift line on the next load.
 *
 * Theme integration notes (Dawn-based custom theme "v5 - Rework Ambroxan"):
 *  - cart_type = drawer (primary UX is <cart-drawer>); the /cart page is also
 *    supported.
 *  - cart-drawer.js installs a window.fetch wrapper inside its DOMContentLoaded
 *    handler (Fix 1.1). This file runs its top-level code BEFORE that handler
 *    fires (both are deferred scripts; DOMContentLoaded fires after all of them
 *    execute their top level), so we capture the NATIVE fetch here and use it
 *    for our own cart mutations. That guarantees our calls bypass every wrapper:
 *    no infinite loop, and no unintended drawer auto-open.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // --- Capture native fetch BEFORE any theme wrapper is installed ------------
  var nativeFetch = window.fetch.bind(window);

  var CONFIG = window.FREE_GIFT || {};
  var GIFT_PROP = '_gift';            // line item property marking the gift line
  var DEBOUNCE_MS = 400;             // coalesce bursts; > cart-drawer.js 250ms open
  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  // Normalise config -> strings for reliable id comparison
  var giftId = CONFIG.giftVariantId ? String(CONFIG.giftVariantId) : '';
  var qualifyingIds = (CONFIG.qualifyingVariantIds || []).map(String);

  // Hard stops: no gift variant configured -> do nothing.
  if (!giftId) { return; }

  var busy = false;          // a mutation+refresh cycle is running
  var queued = false;        // a re-evaluation was requested while busy
  var debounceTimer = null;

  function log() {
    if (CONFIG.debug && window.console && console.log) {
      console.log.apply(console, ['[free-gift]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  /* ===========================================================================
     CART HELPERS (all use nativeFetch -> never re-trigger our own watcher)
     =========================================================================== */

  function getCart() {
    return nativeFetch(window.Shopify.routes.root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // The gift line, identified primarily by its property, with variant id fallback.
  function findGiftItem(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var marked = it.properties && (it.properties[GIFT_PROP] || it.properties['_gift']);
      if (marked || String(it.variant_id) === giftId) { return it; }
    }
    return null;
  }

  // Any non-gift line whose variant id is in the qualifying allowlist.
  function hasQualifier(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var marked = it.properties && (it.properties[GIFT_PROP] || it.properties['_gift']);
      if (marked) { continue; } // never let the gift qualify itself
      if (qualifyingIds.indexOf(String(it.variant_id)) !== -1) { return true; }
    }
    return false;
  }

  function addGift() {
    var props = {};
    props[GIFT_PROP] = 'true';
    log('adding gift', giftId);
    return nativeFetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: Number(giftId), quantity: 1, properties: props })
    });
  }

  // Change a line identified by its robust line-item key (not its index).
  function changeByKey(key, quantity) {
    log('change', key, '->', quantity);
    return nativeFetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  /* ===========================================================================
     UI REFRESH (Sections Rendering API — GET requests, never matched by the
     cart-mutation watcher, so they cannot loop)
     =========================================================================== */

  function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.updateDrawerContent === 'function') {
      return drawer.updateDrawerContent().catch(function () {});
    }
    return Promise.resolve();
  }

  function swapJsContents(wrapper, html) {
    if (!wrapper || !html) { return; }
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('.js-contents');
    var current = wrapper.querySelector('.js-contents');
    if (fresh && current) { current.innerHTML = fresh.innerHTML; }
  }

  // Re-render the /cart page line items + cart bubble. The footer/subtotal never
  // changes (gift is 0€) so we skip it.
  function refreshCartPage() {
    var itemsWrap = document.getElementById('main-cart-items');
    if (!itemsWrap) { return Promise.resolve(); }
    var sectionId = itemsWrap.dataset.id || 'cart-items';
    var url = window.Shopify.routes.root + '?sections=' + encodeURIComponent(sectionId) + ',cart-icon-bubble';
    return nativeFetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        swapJsContents(itemsWrap, data[sectionId]);
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data['cart-icon-bubble']) {
          var d = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          var nb = d.querySelector('#cart-icon-bubble');
          if (nb) { bubble.innerHTML = nb.innerHTML; }
        }
      })
      .catch(function () {});
  }

  function refreshUI() {
    var tasks = [refreshDrawer()];
    if (document.getElementById('main-cart-items')) { tasks.push(refreshCartPage()); }
    return Promise.all(tasks);
  }

  /* ===========================================================================
     CORE EVALUATION
     =========================================================================== */

  function evaluate() {
    if (busy) { queued = true; return; }
    busy = true;

    getCart().then(function (cart) {
      var gift = findGiftItem(cart);
      var qualifies = hasQualifier(cart);

      // Campaign disabled -> tear down any leftover gift, then stop.
      if (!CONFIG.enabled) {
        if (gift) { return changeByKey(gift.key, 0).then(refreshUI); }
        return null;
      }

      if (qualifies && !gift) {
        // Need the gift -> add it.
        return addGift().then(refreshUI);
      }

      if (!qualifies && gift) {
        // Qualifier gone -> remove the gift.
        return changeByKey(gift.key, 0).then(refreshUI);
      }

      if (gift && gift.quantity !== 1) {
        // Defensive: force the gift quantity back to 1.
        return changeByKey(gift.key, 1).then(refreshUI);
      }

      return null; // nothing to do
    })
    .catch(function (e) { log('evaluate error', e); })
    .then(function () {
      busy = false;
      if (queued) { queued = false; scheduleEvaluate(); }
    });
  }

  function scheduleEvaluate() {
    if (debounceTimer) { clearTimeout(debounceTimer); }
    debounceTimer = setTimeout(evaluate, DEBOUNCE_MS);
  }

  /* ===========================================================================
     TRIGGERS — catch every path that can mutate the cart
     =========================================================================== */

  function installFetchWatcher() {
    // At this point (inside our DOMContentLoaded handler, registered after
    // cart-drawer.js) window.fetch is the theme's wrapper. We wrap once more so
    // we observe completed cart mutations from: product forms, AI custom blocks,
    // sticky ATC, the drawer quantity buttons, and the insurance/upsell toggles.
    var previousFetch = window.fetch;
    var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = previousFetch.apply(this, arguments);
      if (url && CART_MUTATION.test(url)) {
        result.then(function (res) { if (res && res.ok) { scheduleEvaluate(); } })
              .catch(function () {});
      }
      return result;
    };
  }

  /* ===========================================================================
     PDP GIFT BLOCK — show the #FreeGiftPdp card only while the selected
     variant qualifies (rendered hidden by snippets/free-gift-pdp.liquid)
     =========================================================================== */

  function initPdpBlock() {
    var block = document.getElementById('FreeGiftPdp');
    if (!block) { return; }

    var update = function (variantId) {
      var show = CONFIG.enabled && variantId && qualifyingIds.indexOf(String(variantId)) !== -1;
      block.hidden = !show;
      block.classList.toggle('free-gift-pdp--visible', !!show);
    };

    // Selected variant = hidden input of the closest product form (works for the
    // Dawn picker AND the custom AI buy-box which both keep input[name="id"] in sync).
    var readSelected = function () {
      var scope = block.closest('form') || document;
      var input = scope.querySelector('input[name="id"]') ||
                  document.querySelector('form[action*="/cart/add"] input[name="id"]');
      return input && input.value;
    };

    // Initial state.
    update(readSelected());

    // 1) Dawn picker publishes variantChange on every pick.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        update(variant && variant.id);
      });
    }

    // 2) Custom buy-boxes (AI blocks) update input[name="id"] silently on card
    //    click — re-read the selection right after any variant-card click.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-variant-id], [data-contenance]')) {
        setTimeout(function () { update(readSelected()); }, 60);
      }
    });
  }

  function init() {
    if (!window.Shopify || !window.Shopify.routes) {
      // Extremely unlikely on a Shopify storefront; fall back to absolute paths.
      window.Shopify = window.Shopify || {};
      window.Shopify.routes = window.Shopify.routes || { root: '/' };
    }

    installFetchWatcher();
    initPdpBlock();

    // Cart page (Dawn) publishes this on its own quantity changes.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleEvaluate(); });
    }

    // Initial pass: handles refresh / navigation / disabled-teardown.
    evaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Restore from bfcache (back/forward) — cart may have changed elsewhere.
  window.addEventListener('pageshow', function (e) { if (e.persisted) { scheduleEvaluate(); } });
})();
